import * as THREE from "three";
import { FIELD_LAYOUTS, ROAD_LAYOUTS } from "./layout";

type Rng = { next: () => number };
type HeightFn = (x: number, z: number) => number;

function rr(rng: Rng, lo: number, hi: number): number {
  return lo + rng.next() * (hi - lo);
}

function distanceToSegment2D(
  px: number,
  pz: number,
  ax: number,
  az: number,
  bx: number,
  bz: number,
): number {
  const abx = bx - ax;
  const abz = bz - az;
  const lenSq = abx * abx + abz * abz;
  const t = lenSq <= 1e-6 ? 0 : THREE.MathUtils.clamp(((px - ax) * abx + (pz - az) * abz) / lenSq, 0, 1);
  const cx = ax + abx * t;
  const cz = az + abz * t;
  return Math.hypot(px - cx, pz - cz);
}

function roadClearanceAt(x: number, z: number): number {
  let clearance = 0;
  for (const road of ROAD_LAYOUTS) {
    for (let i = 0; i < road.points.length - 1; i++) {
      const a = road.points[i]!;
      const b = road.points[i + 1]!;
      const distance = distanceToSegment2D(x, z, a.x, a.z, b.x, b.z);
      const radius = road.width * 1.2;
      if (distance > radius) continue;
      clearance = Math.max(clearance, 1 - distance / radius);
    }
  }
  return clearance;
}

function fieldMaskAt(x: number, z: number): number {
  let mask = 0;
  for (const field of FIELD_LAYOUTS) {
    const cos = Math.cos(field.rotation);
    const sin = Math.sin(field.rotation);
    const lx = (x - field.x) * cos + (z - field.z) * sin;
    const lz = -(x - field.x) * sin + (z - field.z) * cos;
    const hx = field.width * 0.5;
    const hz = field.depth * 0.5;
    if (Math.abs(lx) > hx || Math.abs(lz) > hz) continue;
    const edge = Math.max(Math.abs(lx) / hx, Math.abs(lz) / hz);
    mask = Math.max(mask, 1 - edge);
  }
  return mask;
}

function settlementMaskAt(x: number, z: number): number {
  const dist = Math.hypot(x, z);
  const urban = 1 - THREE.MathUtils.smoothstep(dist, 150, 300);
  const suburban = THREE.MathUtils.smoothstep(dist, 180, 270) * (1 - THREE.MathUtils.smoothstep(dist, 430, 700));
  return Math.max(urban, suburban * 0.7);
}

function moistureAt(x: number, z: number): number {
  const riverX = 500;
  const riverZ = -80;
  const dz = z - riverZ;
  const dx = x - riverX - dz * Math.tan(0.15);
  const river = 1 - THREE.MathUtils.clamp(Math.abs(dx) / 180, 0, 1);
  const basinNoise = (
    Math.sin(x * 0.008) * 0.35 +
    Math.cos(z * 0.01) * 0.4 +
    Math.sin((x + z) * 0.004) * 0.25
  ) * 0.5 + 0.5;
  return THREE.MathUtils.clamp(river * 0.7 + basinNoise * 0.45, 0, 1);
}

function applyMatrix(
  mesh: THREE.InstancedMesh,
  index: number,
  position: THREE.Vector3,
  scale: THREE.Vector3,
  rotationY: number,
  color: THREE.Color,
  dummy: THREE.Object3D,
): void {
  dummy.position.copy(position);
  dummy.scale.copy(scale);
  dummy.rotation.set(0, rotationY, 0);
  dummy.updateMatrix();
  mesh.setMatrixAt(index, dummy.matrix);
  mesh.setColorAt(index, color);
}

function addScatterTrees(group: THREE.Group, rng: Rng, size: number, getHeight: HeightFn): void {
  const half = size * 0.5 - 70;
  const urbanClear = 215;
  const treeTarget = 420;
  const broadTarget = 220;
  const trunkGeo = new THREE.CylinderGeometry(0.22, 0.34, 1, 6);
  const coniferGeo = new THREE.ConeGeometry(1, 1, 7);
  const broadGeo = new THREE.SphereGeometry(1, 8, 6);
  const trunkMat = new THREE.MeshStandardMaterial({
    color: 0x2b2117,
    roughness: 1,
    emissive: new THREE.Color(0x090706),
    emissiveIntensity: 0.22,
  });
  const coniferMat = new THREE.MeshStandardMaterial({
    color: 0x22381d,
    roughness: 0.95,
    emissive: new THREE.Color(0x0b1a0c),
    emissiveIntensity: 0.55,
  });
  const broadMat = new THREE.MeshStandardMaterial({
    color: 0x41502a,
    roughness: 1,
    emissive: new THREE.Color(0x12170d),
    emissiveIntensity: 0.35,
  });

  const coniferTrunks = new THREE.InstancedMesh(trunkGeo, trunkMat, treeTarget);
  const conifers = new THREE.InstancedMesh(coniferGeo, coniferMat, treeTarget);
  const broadTrunks = new THREE.InstancedMesh(trunkGeo, trunkMat, broadTarget);
  const broadTrees = new THREE.InstancedMesh(broadGeo, broadMat, broadTarget);
  const dummy = new THREE.Object3D();
  const pos = new THREE.Vector3();
  const scale = new THREE.Vector3();
  const trunkColor = new THREE.Color();
  const leafColor = new THREE.Color();
  let coniferCount = 0;
  let broadCount = 0;

  for (let attempt = 0; attempt < (treeTarget + broadTarget) * 10 && (coniferCount < treeTarget || broadCount < broadTarget); attempt++) {
    const x = rr(rng, -half, half);
    const z = rr(rng, -half, half);
    const dist = Math.hypot(x, z);
    if (dist < urbanClear) continue;

    const road = roadClearanceAt(x, z);
    const field = fieldMaskAt(x, z);
    const settlement = settlementMaskAt(x, z);
    if (road > 0.18 || field > 0.14 || settlement > 0.8) continue;

    const moisture = moistureAt(x, z);
    const allowBroad = moisture > 0.45 || settlement > 0.18;
    const y = getHeight(x, z);

    if (allowBroad && broadCount < broadTarget && rng.next() > 0.4) {
      const trunkH = rr(rng, 3.6, 6.2);
      const crown = rr(rng, 2.6, 4.8);
      trunkColor.setHex(0x2f2419).offsetHSL(rr(rng, -0.01, 0.01), 0, rr(rng, -0.04, 0.03));
      leafColor.setHex(0x4b5c31).offsetHSL(rr(rng, -0.03, 0.02), rr(rng, -0.04, 0.04), rr(rng, -0.08, 0.05));

      applyMatrix(broadTrunks, broadCount, pos.set(x, y + trunkH * 0.5, z), scale.set(1, trunkH, 1), rng.next() * Math.PI * 2, trunkColor, dummy);
      applyMatrix(broadTrees, broadCount, pos.set(x, y + trunkH + crown * 0.34, z), scale.set(crown, crown * rr(rng, 0.75, 1.08), crown * rr(rng, 0.8, 1.1)), rng.next() * Math.PI * 2, leafColor, dummy);
      broadCount++;
      continue;
    }

    if (coniferCount < treeTarget) {
      const trunkH = rr(rng, 4.2, 7.4);
      const crownH = rr(rng, 7, 12.5);
      const crownR = rr(rng, 2.2, 3.8);
      trunkColor.setHex(0x291f15).offsetHSL(rr(rng, -0.01, 0.01), 0, rr(rng, -0.04, 0.03));
      leafColor.setHex(0x2a4521).offsetHSL(rr(rng, -0.03, 0.02), rr(rng, -0.04, 0.04), rr(rng, -0.08, 0.04));

      applyMatrix(coniferTrunks, coniferCount, pos.set(x, y + trunkH * 0.5, z), scale.set(1, trunkH, 1), rng.next() * Math.PI * 2, trunkColor, dummy);
      applyMatrix(conifers, coniferCount, pos.set(x, y + trunkH + crownH * 0.38, z), scale.set(crownR, crownH, crownR), rng.next() * Math.PI * 2, leafColor, dummy);
      coniferCount++;
    }
  }

  coniferTrunks.count = coniferCount;
  conifers.count = coniferCount;
  broadTrunks.count = broadCount;
  broadTrees.count = broadCount;
  coniferTrunks.instanceMatrix.needsUpdate = true;
  conifers.instanceMatrix.needsUpdate = true;
  broadTrunks.instanceMatrix.needsUpdate = true;
  broadTrees.instanceMatrix.needsUpdate = true;
  if (coniferTrunks.instanceColor) coniferTrunks.instanceColor.needsUpdate = true;
  if (conifers.instanceColor) conifers.instanceColor.needsUpdate = true;
  if (broadTrunks.instanceColor) broadTrunks.instanceColor.needsUpdate = true;
  if (broadTrees.instanceColor) broadTrees.instanceColor.needsUpdate = true;

  group.add(coniferTrunks, conifers, broadTrunks, broadTrees);
}

function addFieldEdgeTrees(group: THREE.Group, rng: Rng, getHeight: HeightFn): void {
  const trunkGeo = new THREE.CylinderGeometry(0.18, 0.28, 1, 6);
  const canopyGeo = new THREE.SphereGeometry(1, 7, 5);
  const trunkMat = new THREE.MeshStandardMaterial({
    color: 0x30261a,
    roughness: 1,
    emissive: new THREE.Color(0x090706),
    emissiveIntensity: 0.18,
  });
  const canopyMat = new THREE.MeshStandardMaterial({
    color: 0x4f6231,
    roughness: 1,
    emissive: new THREE.Color(0x14190d),
    emissiveIntensity: 0.32,
  });
  const target = 180;
  const trunks = new THREE.InstancedMesh(trunkGeo, trunkMat, target);
  const canopies = new THREE.InstancedMesh(canopyGeo, canopyMat, target);
  const dummy = new THREE.Object3D();
  const pos = new THREE.Vector3();
  const scale = new THREE.Vector3();
  const trunkColor = new THREE.Color();
  const leafColor = new THREE.Color();
  let count = 0;

  for (const field of FIELD_LAYOUTS) {
    const longSide = Math.max(field.width, field.depth);
    const step = 18;
    const perSide = Math.max(3, Math.floor(longSide / step));
    const edgeOffset = field.depth > field.width ? field.width * 0.5 + 8 : field.depth * 0.5 + 8;
    for (const side of [-1, 1] as const) {
      for (let i = 0; i < perSide && count < target; i++) {
        const t = perSide === 1 ? 0.5 : i / (perSide - 1);
        const along = THREE.MathUtils.lerp(-longSide * 0.48, longSide * 0.48, t) + rr(rng, -2.2, 2.2);
        const across = side * edgeOffset + rr(rng, -2, 2);

        const localX = field.width >= field.depth ? along : across;
        const localZ = field.width >= field.depth ? across : along;
        const cos = Math.cos(field.rotation);
        const sin = Math.sin(field.rotation);
        const x = field.x + localX * cos - localZ * sin;
        const z = field.z + localX * sin + localZ * cos;
        if (roadClearanceAt(x, z) > 0.15 || fieldMaskAt(x, z) > 0.12) continue;

        const y = getHeight(x, z);
        const trunkH = rr(rng, 3.2, 5.6);
        const crown = rr(rng, 2.2, 3.6);
        trunkColor.setHex(0x312719).offsetHSL(rr(rng, -0.01, 0.01), 0, rr(rng, -0.03, 0.03));
        leafColor.setHex(0x607241).offsetHSL(rr(rng, -0.03, 0.02), rr(rng, -0.04, 0.03), rr(rng, -0.05, 0.04));

        applyMatrix(trunks, count, pos.set(x, y + trunkH * 0.5, z), scale.set(1, trunkH, 1), rng.next() * Math.PI * 2, trunkColor, dummy);
        applyMatrix(canopies, count, pos.set(x, y + trunkH + crown * 0.3, z), scale.set(crown, crown * rr(rng, 0.8, 1.05), crown), rng.next() * Math.PI * 2, leafColor, dummy);
        count++;
      }
    }
  }

  trunks.count = count;
  canopies.count = count;
  trunks.instanceMatrix.needsUpdate = true;
  canopies.instanceMatrix.needsUpdate = true;
  if (trunks.instanceColor) trunks.instanceColor.needsUpdate = true;
  if (canopies.instanceColor) canopies.instanceColor.needsUpdate = true;
  group.add(trunks, canopies);
}

function addScrub(group: THREE.Group, rng: Rng, size: number, getHeight: HeightFn): void {
  const half = size * 0.5 - 60;
  const target = 900;
  const scrubGeo = new THREE.IcosahedronGeometry(1, 0);
  const scrubMat = new THREE.MeshStandardMaterial({
    color: 0x566237,
    roughness: 1,
    emissive: new THREE.Color(0x17180d),
    emissiveIntensity: 0.18,
  });
  const scrubMesh = new THREE.InstancedMesh(scrubGeo, scrubMat, target);
  const dummy = new THREE.Object3D();
  const pos = new THREE.Vector3();
  const scale = new THREE.Vector3();
  const color = new THREE.Color();
  let count = 0;

  for (let attempt = 0; attempt < target * 6 && count < target; attempt++) {
    const x = rr(rng, -half, half);
    const z = rr(rng, -half, half);
    const settlement = settlementMaskAt(x, z);
    const road = roadClearanceAt(x, z);
    const field = fieldMaskAt(x, z);
    if (settlement > 0.92 || road > 0.35 || field > 0.42) continue;

    const moisture = moistureAt(x, z);
    const y = getHeight(x, z);
    const radius = rr(rng, 1.4, 3.6) * (1 + moisture * 0.2);
    const lift = rr(rng, 0.35, 0.85);
    color.setHex(0x59663a).offsetHSL(rr(rng, -0.03, 0.02), rr(rng, -0.04, 0.03), rr(rng, -0.08, 0.04));

    applyMatrix(scrubMesh, count, pos.set(x, y + lift, z), scale.set(radius, lift * rr(rng, 0.8, 1.4), radius * rr(rng, 0.75, 1.2)), rng.next() * Math.PI * 2, color, dummy);
    count++;
  }

  scrubMesh.count = count;
  scrubMesh.instanceMatrix.needsUpdate = true;
  if (scrubMesh.instanceColor) scrubMesh.instanceColor.needsUpdate = true;
  group.add(scrubMesh);
}

export function addVegetation(group: THREE.Group, rng: Rng, size: number, getHeight: HeightFn): void {
  addScatterTrees(group, rng, size, getHeight);
  addFieldEdgeTrees(group, rng, getHeight);
  addScrub(group, rng, size, getHeight);
}
